import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { UserPlus, Shield, Trash2, Key, Mail, User, AlertCircle, CheckCircle, Eye, EyeOff, Share2, FileText, Target, BarChart3, Settings } from 'lucide-react';

import { BACKEND_URL } from '../utils/api';
const API = `${BACKEND_URL}/api`;

// Available roles with descriptions
const ROLES = [
  { id: 'viewer', label: 'Viewer', description: 'View dashboard only', color: 'bg-slate-100 text-slate-700' },
  { id: 'social_publisher', label: 'Social Publisher', description: 'Social media publishing only', color: 'bg-purple-100 text-purple-700' },
  { id: 'editor', label: 'Editor', description: 'Content & social publishing', color: 'bg-blue-100 text-blue-700' },
  { id: 'manager', label: 'Manager', description: 'Leads, email campaigns, content', color: 'bg-green-100 text-green-700' },
  { id: 'admin', label: 'Admin', description: 'Full access except user management', color: 'bg-orange-100 text-orange-700' },
  { id: 'super_admin', label: 'Super Admin', description: 'Full system access', color: 'bg-red-100 text-red-700' },
];

// Available permission modules
const PERMISSIONS = [
  { id: 'social_publisher', label: 'Social Publisher', icon: Share2, description: 'Publish to Facebook, Instagram, TikTok' },
  { id: 'content_management', label: 'Content Management', icon: FileText, description: 'Blog posts, press releases, portfolio' },
  { id: 'lead_management', label: 'Lead Management', icon: Target, description: 'View and manage leads' },
  { id: 'email_campaigns', label: 'Email Campaigns', icon: Mail, description: 'Email marketing tools' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, description: 'SEO and performance analytics' },
  { id: 'user_management', label: 'User Management', icon: User, description: 'Manage admin users' },
  { id: 'system_settings', label: 'System Settings', icon: Settings, description: 'Configure system processes' },
];

// Available clients for multi-tenant access
const CLIENTS = [
  { id: 'web-techservices', name: 'Web-TechServices' },
  { id: 'fortworthdental', name: 'Fort Worth Dental' },
  { id: '1staveicp', name: '1st Ave Ice Cream Parlor' },
  { id: 'lazydayshome', name: 'Lazy Days Home' },
  { id: 'overhaulops', name: 'Overhaul Ops' },
  { id: 'politicalliars', name: 'Political Liars' },
];

function AdminUserManagement({ adminToken }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    email: '',
    password: '',
    role: 'viewer',
    permissions: [],
    allowed_clients: [],
    is_super_admin: false
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAdmins(response.data.admins || []);
    } catch (err) {
      setError('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (roleId) => {
    // Auto-set permissions based on role
    const rolePermissions = {
      'viewer': [],
      'social_publisher': ['social_publisher'],
      'editor': ['social_publisher', 'content_management'],
      'manager': ['social_publisher', 'content_management', 'lead_management', 'email_campaigns'],
      'admin': ['social_publisher', 'content_management', 'lead_management', 'email_campaigns', 'analytics', 'user_management'],
      'super_admin': PERMISSIONS.map(p => p.id)
    };
    
    setNewAdmin({
      ...newAdmin,
      role: roleId,
      permissions: rolePermissions[roleId] || [],
      is_super_admin: roleId === 'super_admin'
    });
  };

  const togglePermission = (permId) => {
    const perms = newAdmin.permissions.includes(permId)
      ? newAdmin.permissions.filter(p => p !== permId)
      : [...newAdmin.permissions, permId];
    setNewAdmin({ ...newAdmin, permissions: perms, role: 'custom' });
  };

  const toggleClient = (clientId) => {
    const clients = newAdmin.allowed_clients.includes(clientId)
      ? newAdmin.allowed_clients.filter(c => c !== clientId)
      : [...newAdmin.allowed_clients, clientId];
    setNewAdmin({ ...newAdmin, allowed_clients: clients });
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await axios.post(
        `${API}/admin/users/create`,
        newAdmin,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      setSuccess('Admin user created successfully!');
      setNewAdmin({ username: '', email: '', password: '', role: 'viewer', permissions: [], allowed_clients: [], is_super_admin: false });
      setShowAddForm(false);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create admin user');
    }
  };

  const handleDeleteAdmin = async (username) => {
    if (!confirm(`Are you sure you want to delete admin user "${username}"?`)) return;

    try {
      await axios.delete(`${API}/admin/users/${username}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSuccess(`Admin user "${username}" deleted successfully`);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete admin user');
    }
  };

  const handleResetPassword = async (username) => {
    const newPassword = prompt(`Enter new password for "${username}":`);
    if (!newPassword || newPassword.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/users/${username}/reset-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setSuccess(`Password reset for "${username}" successful`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const getRoleBadge = (admin) => {
    const role = ROLES.find(r => r.id === admin.role) || ROLES[0];
    if (admin.is_super_admin) {
      return <Badge className="bg-red-100 text-red-700"><Shield className="h-3 w-3 mr-1" />Super Admin</Badge>;
    }
    return <Badge className={role.color}>{role.label}</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading admin users...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Admin User Management</h2>
          <p className="text-slate-600">Manage admin accounts with role-based permissions</p>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)} className="bg-blue-600 hover:bg-blue-700">
          <UserPlus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-start gap-2">
          <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-green-700">×</button>
        </div>
      )}

      {/* Add Admin Form */}
      {showAddForm && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle>Create New User</CardTitle>
            <CardDescription>Add a new user with specific role and permissions</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddAdmin} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    <User className="inline h-4 w-4 mr-1" />
                    Username
                  </label>
                  <Input
                    type="text"
                    placeholder="johndoe"
                    value={newAdmin.username}
                    onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
                    required
                    minLength={3}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    <Mail className="inline h-4 w-4 mr-1" />
                    Email
                  </label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    <Key className="inline h-4 w-4 mr-1" />
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min 8 characters"
                      value={newAdmin.password}
                      onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                      required
                      minLength={8}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Role Selection */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700">Role</label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                  {ROLES.map(role => (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => handleRoleChange(role.id)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        newAdmin.role === role.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="font-medium text-sm">{role.label}</div>
                      <div className="text-xs text-slate-500 mt-1">{role.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Permissions */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700">
                  Permissions {newAdmin.role !== 'super_admin' && '(customize)'}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {PERMISSIONS.map(perm => {
                    const Icon = perm.icon;
                    const isSelected = newAdmin.permissions.includes(perm.id);
                    return (
                      <button
                        key={perm.id}
                        type="button"
                        onClick={() => togglePermission(perm.id)}
                        disabled={newAdmin.role === 'super_admin'}
                        className={`p-3 rounded-lg border text-left transition-all flex items-start gap-2 ${
                          isSelected
                            ? 'border-green-500 bg-green-50'
                            : 'border-slate-200 hover:border-slate-300'
                        } ${newAdmin.role === 'super_admin' ? 'opacity-50' : ''}`}
                      >
                        <Icon className={`h-4 w-4 mt-0.5 ${isSelected ? 'text-green-600' : 'text-slate-400'}`} />
                        <div>
                          <div className="font-medium text-xs">{perm.label}</div>
                          <div className="text-xs text-slate-500">{perm.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Client Access (for Social Publisher role) */}
              {newAdmin.permissions.includes('social_publisher') && (
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Client Access <span className="text-slate-400 font-normal">(leave empty for all clients)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CLIENTS.map(client => (
                      <button
                        key={client.id}
                        type="button"
                        onClick={() => toggleClient(client.id)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          newAdmin.allowed_clients.includes(client.id)
                            ? 'border-blue-500 bg-blue-100 text-blue-700'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        {client.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Submit */}
              <div className="flex gap-2 pt-4 border-t">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Create User
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewAdmin({ username: '', email: '', password: '', role: 'viewer', permissions: [], allowed_clients: [], is_super_admin: false });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Quick Add: Meta Reviewer */}
      {!showAddForm && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-purple-900">Quick Add: Meta App Reviewer</p>
                <p className="text-sm text-purple-700">Create a Social Publisher-only account for Meta app review</p>
              </div>
              <Button
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
                onClick={() => {
                  setNewAdmin({
                    username: 'meta-reviewer',
                    email: 'meta-reviewer@web-techservices.com',
                    password: 'MetaReview2026!',
                    role: 'social_publisher',
                    permissions: ['social_publisher'],
                    allowed_clients: [],
                    is_super_admin: false
                  });
                  setShowAddForm(true);
                }}
              >
                <Shield className="mr-2 h-4 w-4" />
                Create Meta Reviewer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Current Users ({admins.length})</CardTitle>
          <CardDescription>All users with access to this system</CardDescription>
        </CardHeader>
        <CardContent>
          {admins.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No admin users found</p>
          ) : (
            <div className="space-y-3">
              {admins.map((admin) => (
                <div
                  key={admin.username || admin.email}
                  className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900">{admin.username || admin.email}</h3>
                        {getRoleBadge(admin)}
                        {admin.two_factor_enabled && (
                          <Badge className="bg-green-100 text-green-700">2FA</Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-slate-600 mb-2">
                        <Mail className="inline h-4 w-4 mr-1" />
                        {admin.email}
                      </p>

                      {/* Show permissions */}
                      {admin.permissions && admin.permissions.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {admin.permissions.slice(0, 4).map(perm => (
                            <span key={perm} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                              {perm.replace('_', ' ')}
                            </span>
                          ))}
                          {admin.permissions.length > 4 && (
                            <span className="text-xs text-slate-500">+{admin.permissions.length - 4} more</span>
                          )}
                        </div>
                      )}

                      {/* Show allowed clients */}
                      {admin.allowed_clients && admin.allowed_clients.length > 0 && (
                        <p className="text-xs text-slate-500">
                          Clients: {admin.allowed_clients.join(', ')}
                        </p>
                      )}
                      
                      <div className="text-xs text-slate-400 mt-2">
                        {admin.created_at && `Created: ${new Date(admin.created_at).toLocaleDateString()}`}
                        {admin.last_login && ` • Last login: ${new Date(admin.last_login).toLocaleDateString()}`}
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResetPassword(admin.username)}
                        title="Reset Password"
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteAdmin(admin.username)}
                        title="Delete User"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Descriptions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Role Descriptions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            {ROLES.map(role => (
              <div key={role.id} className="flex items-start gap-2">
                <Badge className={`${role.color} shrink-0`}>{role.label}</Badge>
                <span className="text-slate-600">{role.description}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AdminUserManagement;
